import React from 'react';

/**
 * Object URLs for media the thread has already fetched, keyed by wamid.
 *
 * ★ WHY A REGISTRY. `useMediaObjectUrl` fetches a photo through the
 * authenticated proxy and revokes the blob URL when its bubble unmounts. The
 * lightbox, the quoted-reply thumbnail and the contact panel's media grid all
 * want the same bytes; without this each would fetch them again. Bubbles
 * register on ready and unregister on unmount, so an entry here is always a
 * live URL.
 *
 * Nothing is fetched here — this only remembers what a bubble already has.
 */
const registry = new Map();
const listeners = new Set();

const notify = () => listeners.forEach((l) => l());

export function registerMedia(wamid, entry) {
	if (!wamid || !entry?.url) return;
	registry.set(wamid, entry);
	notify();
}

export function unregisterMedia(wamid) {
	if (registry.delete(wamid)) notify();
}

export function getMedia(wamid) {
	return registry.get(wamid) ?? null;
}

/** Everything registered for one thread, oldest first. */
export function listMedia(conversationId) {
	return [...registry.entries()]
		.map(([wamid, entry]) => ({ wamid, ...entry }))
		.filter((e) => !conversationId || String(e.conversationId) === String(conversationId))
		.sort((a, b) => new Date(a.occurredAt) - new Date(b.occurredAt));
}

function useRegistryVersion() {
	const [version, force] = React.useReducer((n) => n + 1, 0);
	React.useEffect(() => {
		listeners.add(force);
		return () => listeners.delete(force);
	}, []);
	return version;
}

/** Re-renders when the registry changes; returns the lookup. */
export function useMediaRegistry() {
	useRegistryVersion();
	return getMedia;
}

/** The thread's loaded media as a list that re-renders as bubbles load. */
export function useMediaList(conversationId) {
	const version = useRegistryVersion();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	return React.useMemo(() => listMedia(conversationId), [conversationId, version]);
}
